import {
  AssistantProcess,
  AssistantProcessStatus,
  BillingInterval,
  ComponentConfigurationType,
  ComponentType,
  Device,
  Document,
  DocumentAnswer,
  DocumentVersion,
  Entitlement,
  EntitlementType,
  File,
  Organization,
  OrganizationMemberWithOrganization,
  Product,
  Subscription,
  SubscriptionItem,
  TEMPLATE_ID,
  User,
} from "@models";
import {
  AnswerItem,
  AssistantMultiStepFormStep,
  ChangeSuggestionRequestBody,
  DocumentDataKey,
  RegulatoryFunctionalGroup,
  RegulatoryTypeEnum,
  Suggestion,
  SuggestionRequestBody,
  TemplateConfig,
  TemplateElement,
} from "@types";
import {
  hasHardware,
  hasSoftware,
  hasSterileComponent,
} from "src/utils/device";

export const mockRoadmapTasks = [
  {
    id: "task-1",
    title: "Post Market Surveillance Report",
    taskKey: TEMPLATE_ID.POST_MARKET_SURVEILLANCE_REPORT,
    parentCardKey: "post-market-surveillance",
    enabled: true,
    createdAt: new Date(),
    updatedAt: new Date(),
    visibilityConditions: {
      isClass1: true,
    },
  },
];

export const mockTemplateConfig: TemplateConfig = {
  [TEMPLATE_ID.INTENDED_USE]: {
    templateKey: TEMPLATE_ID.INTENDED_USE,
    id: "intended-use",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.TD,
    regulatoryType: RegulatoryTypeEnum.TCD,
    defaultTitle: "Intended Use",
    elements: [
      {
        id: "intended-use",
        prompt: true,
        required: true,
        element: {
          id: "intended-use",
          type: "textField",
          options: {
            label: "What is the intended use for the device?",
            rows: 5,
            helperText: `Your intended use describes how your product functions at its core. What you described in your intended use statement MUST be defendable by data and will end up going into your instructions manual so it is important to phrase this statement carefully so you don't promise too much or too little.`,
          },
        },
      },
      {
        id: "indications-for-use",
        context: ["intended-use"],
        prompt: true,
        required: true,
        element: {
          id: "indications-for-use",
          type: "textField",
          options: {
            label: "What are the indications for use for the device?",
            rows: 5,
            helperText:
              "Indications for use describe the medical condition(s) and/or disease(s) to be screened, monitored, treated, diagnosed, or prevented by the device. This can include exclusion statements about which patients should be excluded but only when necessary.",
          },
        },
      },
      {
        id: "contraindications",
        context: ["intended-use", "indications-for-use"],
        prompt: true,
        required: true,
        element: {
          id: "contraindications",
          type: "textField",
          options: {
            label: "What are the contraindications for the device?",
            rows: 5,
            helperText:
              "Contraindications are medical indications or patients that should be excluded from using your device. This will also go into your instructions manual so be specific. This can include pregnant women, patients with epilepsy, etc.",
          },
        },
      },
      {
        id: "patient-population",
        context: ["contraindications"],
        prompt: true,
        required: true,
        element: {
          id: "patient-population",
          type: "textField",
          options: {
            label: "What patient populations is your device intended for?",
            rows: 5,
            helperText:
              "Describe the patients that will be receiving the benefit of your device. You can include particular ages, health conditions or characteristics, etc. If it is broadly applicable to all people it could be better to keep your patient population more vague and high level. This description can and often does overlap with your user profile depending on how your device is being used.",
          },
        },
      },
      {
        id: "user-profile",
        context: ["contraindications", "patient-population"],
        prompt: true,
        required: true,
        element: {
          id: "user-profile",
          type: "textField",
          options: {
            label:
              "What types of users do you intended to have use your device?",
            rows: 5,
            helperText:
              "Provide a description of your typical users of your software. This can be a regular every day consumer or it could be a medical professional. If they have any specific qualifications or proficiencies in order to use the device make sure to include those as well.",
          },
        },
      },

      {
        id: "use-environment",
        context: ["contraindications", "patient-population", "user-profile"],
        prompt: true,
        required: true,
        element: {
          id: "use-environment",
          type: "textField",
          options: {
            label: "What is the intended use environment for the device?",
            rows: 5,
            helperText:
              "Describe the typical use environment. What sort of devices is this running on? Does the software only run on one device or multiple devices? Is it loud and chaotic like in an emergency ward? How's the lighting? Also, add other software or hardware which is required the your device. Most commonly, apps require users to have a smartphone with a compatible operating system (iOS / Android).",
          },
        },
      },
      {
        id: "principles-of-operation",
        context: [
          "intended-use",
          "indications-for-use",
          "contraindications",
          "patient-population",
          "user-profile",
          "use-environment",
        ],
        prompt: true,
        required: true,
        element: {
          id: "principles-of-operation",
          type: "textField",
          options: {
            label: "What are your devices principles of operation?",
            rows: 5,
            helperText:
              "Provide a very basic principle of operation for your device. What does it receive as information and what does it output? Does it transform information or perhaps cause mechanical changes as part of its operation? Give a very brief overview of how it works when operated correctly.",
          },
        },
      },
      {
        id: "part-of-the-body",
        context: [
          "intended-use",
          "indications-for-use",
          "contraindications",
          "patient-population",
          "user-profile",
          "use-environment",
          "principles-of-operation",
        ],
        prompt: true,
        required: true,
        element: {
          id: "part-of-the-body",
          type: "textField",
          options: {
            label:
              "What parts of the body does your device interact with if any?",
            rows: 5,
            helperText:
              "If your device comes in contact with the body, please describe where and how it interacts with the body. Make sure to include the type of human tissue it comes into contact with such as intact skin, open wounds, muscular tissue, blood, etc.",
          },
        },
      },
      {
        id: "variants-and-accessories",
        required: true,
        element: {
          id: "variants-and-accessories",
          type: "textField",
          options: {
            label:
              "Describe briefly any variants and/or accessories to your device.",
            rows: 5,
            helperText:
              "Provide a description of any different kinds of variants of your device that may provide different functionalities. Also, are there accessories of your device that are core to its function? Describe those as well. If there are none (often there are not) then just say that the device does not have any variants or accessories.",
          },
        },
      },
    ],
  },
  [TEMPLATE_ID.QUALITY_MANUAL_POLICY_OBJECTIVES]: {
    templateKey: TEMPLATE_ID.QUALITY_MANUAL_POLICY_OBJECTIVES,
    id: "quality-manual-policy-objectives",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.TCD,
    defaultTitle: "Quality Manual",
    isOrgDocument: true,
    dependencies: ["management-team"],
    elements: [
      {
        id: "role-of-company",
        required: true,
        element: {
          id: "role-of-company",
          type: "select",
          options: {
            label: "What is the role of the company?",
            options: [
              "Manufacturer",
              "Authorised Representative",
              "Distributor",
            ],
            multiple: true,
            default: "Manufacturer",
            helperText:
              "-Manufacturer: natural or legal person with responsibility for design and/or manufacture of a medical device with the intention of making the medical device available for use, under his name; whether or not such a medical device is designed and/or manufactured by that person himself or on his behalf by another person(s)\n-Authorized representative: natural or legal person established within a country or jurisdiction who has received a written mandate from the manufacturer to act on his behalf for specified tasks with regard to the latter's obligations under that country or jurisdiction's legislation\n-Distributer: natural or legal person in the supply chain who, on his own behalf, furthers the availability of a medical device to the end user (Note: More than one distributor may be involved in the supply chain and persons in the supply chain involved in activities such as storage and transport on behalf of the manufacturer, importer or distributor, are not distributors under this definition.)",
          },
        },
      },
      {
        id: "quality-representative",
        required: true,
        prompt: false,
        element: {
          id: "quality-representative",
          type: "textField",
          options: {
            label:
              "Who is the member from your top management team responsibility for quality management?",

            helperText: `This is usually the VP of Quality or higher designation.

Note: This is the person who FDA sends warning letters to and will be held liable if there are compliance issues. In a startup it is recommend that it is a member of the C-suite. Even if a junior employee is made the management representative, by Responsible Corporate Officer Doctrine (also known as the Park Doctrine), the regulators can hold high-ranking executives liable, even if they were unaware of the illegal activity. So this is an important role and select this person with utmost care and consideration.`,
          },
        },
      },
      {
        id: "quality-policy",
        prompt: true,
        required: true,
        element: {
          id: "quality-policy",
          type: "textField",
          options: {
            label: "What is the quality policy for your company?",
            rows: 5,
            helperText:
              "Describe what your company is about, specifically, its mission and things which are important for it. Maybe you're developing software for patients with a certain disease and your goal is to improve their lives. In addition, the policy should include a commitment to meet legal requirements, keep the QMS up to date and define quality objectives to work towards.",
          },
        },
      },
      {
        id: "quality-objectives",
        prompt: true,
        required: true,
        context: ["quality-policy"],
        element: {
          id: "quality-objectives",
          type: "textField",
          options: {
            label: "What are the quality objectives of your company?",
            rows: 5,
            helperText:
              "Quality objectives shall be measurable and consistent with the quality policy. Those objectives should not (only) refer to the quality of your devices but the quality of your QMS and the overall work of your organization. Typical examples are: hiring excellence in staff, providing, best-of-class device performance, high standards of customer satisfaction, etc.\n Try to define measurable, yet still abstract quality objectives here. In a next step, those are narrowed down to very concrete measures (for example: monthly number of user complaints). This way you don't need to update your quality manual and objectives a lot. And you can re-use a system that you already have, combining QMS with your regular business goals.",
          },
        },
      },
    ],
  },
  [TEMPLATE_ID.SOP_DOCUMENT_RECORD_CONTROL]: {
    templateKey: TEMPLATE_ID.SOP_DOCUMENT_RECORD_CONTROL,
    id: "sop-document-record-control",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Document and Record Control",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_HUMAN_RESOURCES_ADMINISTRATION]: {
    templateKey: TEMPLATE_ID.SOP_HUMAN_RESOURCES_ADMINISTRATION,
    id: "sop-human-resources-administration",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.CP,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Human Resources and Training",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_UPDATE_OF_REGULATIONS]: {
    templateKey: TEMPLATE_ID.SOP_UPDATE_OF_REGULATIONS,
    id: "sop-update-of-regulations",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Regulatory Strategy",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.ADDITIONAL_SOFTWARE_TEST_PLANS]: {
    templateKey: TEMPLATE_ID.ADDITIONAL_SOFTWARE_TEST_PLANS,
    id: "additional-software-test-plans",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Additional Software Test Plans",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.ADDITIONAL_SOFTWARE_TEST_REPORTS]: {
    templateKey: TEMPLATE_ID.ADDITIONAL_SOFTWARE_TEST_REPORTS,
    id: "additional-software-test-reports",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Additional Software Test Reports",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.AUDIT_PLAN]: {
    templateKey: TEMPLATE_ID.AUDIT_PLAN,
    id: "audit-plan",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Audit Plan",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.AUDIT_PROGRAM]: {
    templateKey: TEMPLATE_ID.AUDIT_PROGRAM,
    id: "audit-program",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Audit Program",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.AUDIT_REPORT]: {
    templateKey: TEMPLATE_ID.AUDIT_REPORT,
    id: "audit-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Audit Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.BUG_FIX_REPORT]: {
    templateKey: TEMPLATE_ID.BUG_FIX_REPORT,
    id: "bug-fix-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Bug Fix Report",
    elements: [
      {
        id: "description" as any,
        prompt: true,
        required: true,
        element: {
          id: "description" as any,
          type: "textField",
          options: {
            label: "Description of the bug fix",
            rows: 5,
            helperText:
              "Describe the bug that was fixed and how it was resolved.",
          },
        },
      },
      {
        id: "bug-preventive" as any,
        prompt: true,
        required: true,
        element: {
          id: "bug-preventive" as any,
          type: "textField",
          options: {
            label: "Preventive measures",
            rows: 3,
            helperText: "Describe preventive measures to avoid similar bugs.",
          },
        },
      },
    ],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.CAPA_PLAN]: {
    templateKey: TEMPLATE_ID.CAPA_PLAN,
    id: "capa-plan",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "CAPA Plan",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.CAPA_REPORT]: {
    templateKey: TEMPLATE_ID.CAPA_REPORT,
    id: "capa-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "CAPA Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.CHANGE_EVALUATION_REPORT]: {
    templateKey: TEMPLATE_ID.CHANGE_EVALUATION_REPORT,
    id: "change-evaluation-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Change Evaluation Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.CHANGE_REQUEST]: {
    templateKey: TEMPLATE_ID.CHANGE_REQUEST,
    id: "change-request",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Change Request",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.CHECKLIST_SOFTWARE_RELEASE]: {
    templateKey: TEMPLATE_ID.CHECKLIST_SOFTWARE_RELEASE,
    id: "checklist-software-release",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Checklist Software Release",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.CLINICAL_EVALUATION_PLAN]: {
    templateKey: TEMPLATE_ID.CLINICAL_EVALUATION_PLAN,
    id: "clinical-evaluation-plan",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Clinical Evaluation Plan",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.CLINICAL_EVALUATION_REPORT]: {
    templateKey: TEMPLATE_ID.CLINICAL_EVALUATION_REPORT,
    id: "clinical-evaluation-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Clinical Evaluation Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.DECLARATION_OF_CONFORMITY]: {
    templateKey: TEMPLATE_ID.DECLARATION_OF_CONFORMITY,
    id: "declaration-of-conformity",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Declaration of Conformity",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.DEPLOYMENT_EVALUATION_CHECKLIST]: {
    templateKey: TEMPLATE_ID.DEPLOYMENT_EVALUATION_CHECKLIST,
    id: "deployment-evaluation-checklist",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Deployment Evaluation Checklist",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.DEVICE_LABEL]: {
    templateKey: TEMPLATE_ID.DEVICE_LABEL,
    id: "device-label",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Device Label",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.FIELD_SAFETY_NOTICE]: {
    templateKey: TEMPLATE_ID.FIELD_SAFETY_NOTICE,
    id: "field-safety-notice",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Field Safety Notice",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.GROUP_TRAINING_RECORD]: {
    templateKey: TEMPLATE_ID.GROUP_TRAINING_RECORD,
    id: "group-training-record",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Group Training Record",
    elements: [
      {
        id: "group-training-information",
        prompt: true,
        required: true,
        element: {
          id: "group-training-information",
          type: "table",
          options: {
            label: "Training Information",
            columns: [{ name: "Training Information" }, { name: "Details" }],
          },
        },
      },
    ],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.GSPR_CHECKLIST]: {
    templateKey: TEMPLATE_ID.GSPR_CHECKLIST,
    id: "gspr-checklist",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "GSPR Checklist",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.INCIDENT_ASSESSMENT_FORM]: {
    templateKey: TEMPLATE_ID.INCIDENT_ASSESSMENT_FORM,
    id: "incident-assessment-form",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Incident Assessment Form",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.INSTRUCTIONS_FOR_USE]: {
    templateKey: TEMPLATE_ID.INSTRUCTIONS_FOR_USE,
    id: "instructions-for-use",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Instructions for Use",
    elements: [],
    isOrgDocument: true,
  },

  [TEMPLATE_ID.LIST_CAPA]: {
    templateKey: TEMPLATE_ID.LIST_CAPA,
    id: "list-capa",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "List of CAPAs",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.LIST_OF_EMPLOYEE_TRAINING]: {
    templateKey: TEMPLATE_ID.LIST_OF_EMPLOYEE_TRAINING,
    id: "list-of-employee-training",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "List of Employee Training",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.LIST_OF_KNOWN_ANOMALIES]: {
    templateKey: TEMPLATE_ID.LIST_OF_KNOWN_ANOMALIES,
    id: "list-of-known-anomalies",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "List of Known Anomalies",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.LIST_OF_MEDICAL_DEVICES]: {
    templateKey: TEMPLATE_ID.LIST_OF_MEDICAL_DEVICES,
    id: "list-of-medical-devices",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "List of Medical Devices",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.LIST_OF_REGULATORY_REQUIREMENTS]: {
    templateKey: TEMPLATE_ID.LIST_OF_REGULATORY_REQUIREMENTS,
    id: "list-of-regulatory-requirements",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "List of Regulatory Requirements",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.LIST_OF_VALIDATED_SOFTWARE]: {
    templateKey: TEMPLATE_ID.LIST_OF_VALIDATED_SOFTWARE,
    id: "list-of-validated-software",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "List of Validated Software",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.MANAGEMENT_REVIEW_REPORT]: {
    templateKey: TEMPLATE_ID.MANAGEMENT_REVIEW_REPORT,
    id: "management-review-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Management Review Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.MARKETING_CONTENT]: {
    templateKey: TEMPLATE_ID.MARKETING_CONTENT,
    id: "marketing-content",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Marketing Content",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.OTHER_LANGUAGES_TRANSLATED_IFUS]: {
    templateKey: TEMPLATE_ID.OTHER_LANGUAGES_TRANSLATED_IFUS,
    id: "other-languages-translated-ifus",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Other Languages Translated IFUs",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.PERIODIC_SAFETY_UPDATE_REPORT]: {
    templateKey: TEMPLATE_ID.PERIODIC_SAFETY_UPDATE_REPORT,
    id: "periodic-safety-update-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Periodic Safety Update Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.PHYSICIANS_HANDBOOK]: {
    templateKey: TEMPLATE_ID.PHYSICIANS_HANDBOOK,
    id: "physicians-handbook",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Physicians Handbook",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.POST_MARKET_CLINICAL_FOLLOW_UP_PLAN]: {
    templateKey: TEMPLATE_ID.POST_MARKET_CLINICAL_FOLLOW_UP_PLAN,
    id: "post-market-clinical-follow-up-plan",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Post Market Clinical Follow Up Plan",
    elements: [],
    isOrgDocument: true,
    dependencies: ["expected-lifetime"],
  },
  [TEMPLATE_ID.POST_MARKET_CLINICAL_FOLLOW_UP_REPORT]: {
    templateKey: TEMPLATE_ID.POST_MARKET_CLINICAL_FOLLOW_UP_REPORT,
    id: "post-market-clinical-follow-up-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Post Market Clinical Follow Up Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.POST_MARKET_SURVEILLANCE_PLAN]: {
    templateKey: TEMPLATE_ID.POST_MARKET_SURVEILLANCE_PLAN,
    id: "post-market-surveillance-plan",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Post Market Surveillance Plan",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.POST_MARKET_SURVEILLANCE_REPORT]: {
    templateKey: TEMPLATE_ID.POST_MARKET_SURVEILLANCE_REPORT,
    id: "post-market-surveillance-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Post Market Surveillance Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.PRIMARY_LANGUAGE_TRANSLATED_IFU]: {
    templateKey: TEMPLATE_ID.PRIMARY_LANGUAGE_TRANSLATED_IFU,
    id: "primary-language-translated-ifu",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Primary Language Translated IFU",
    elements: [],
    isOrgDocument: true,
  },

  [TEMPLATE_ID.RELEASE_NOTES]: {
    templateKey: TEMPLATE_ID.RELEASE_NOTES,
    id: "release-notes",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Release Notes",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.REQUIRED_DOCUMENTATION_TRAINING_BY_FUNCTIONAL_GROUP]: {
    templateKey:
      TEMPLATE_ID.REQUIRED_DOCUMENTATION_TRAINING_BY_FUNCTIONAL_GROUP,
    id: "required-documentation-training-by-functional-group",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Required Documentation Training by Functional Group",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.RISK_MANAGEMENT_PLAN]: {
    templateKey: TEMPLATE_ID.RISK_MANAGEMENT_PLAN,
    id: "risk-management-plan",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Risk Management Plan",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.RISK_MANAGEMENT_REPORT]: {
    templateKey: TEMPLATE_ID.RISK_MANAGEMENT_REPORT,
    id: "risk-management-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Risk Management Report",
    elements: [],
    isOrgDocument: true,
  },

  [TEMPLATE_ID.SOFTWARE_DEVELOPMENT_AND_MAINTENANCE_PLAN]: {
    templateKey: TEMPLATE_ID.SOFTWARE_DEVELOPMENT_AND_MAINTENANCE_PLAN,
    id: "software-development-and-maintenance-plan",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Software Development and Maintenance Plan",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOFTWARE_REQUIREMENTS_REVIEW_CHECKLIST]: {
    templateKey: TEMPLATE_ID.SOFTWARE_REQUIREMENTS_REVIEW_CHECKLIST,
    id: "software-requirements-review-checklist",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Software Requirements Review Checklist",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOFTWARE_SYSTEM_TEST_REPORT]: {
    templateKey: TEMPLATE_ID.SOFTWARE_SYSTEM_TEST_REPORT,
    id: "software-system-test-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Software System Test Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOFTWARE_TEST_PLAN]: {
    templateKey: TEMPLATE_ID.SOFTWARE_TEST_PLAN,
    id: "software-test-plan",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Software Test Plan",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOFTWARE_VALIDATION_FORM]: {
    templateKey: TEMPLATE_ID.SOFTWARE_VALIDATION_FORM,
    id: "software-validation-form",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Software Validation Form",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_CAPA]: {
    templateKey: TEMPLATE_ID.SOP_CAPA,
    id: "sop-capa",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP CAPA",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_CHANGE_MANAGEMENT]: {
    templateKey: TEMPLATE_ID.SOP_CHANGE_MANAGEMENT,
    id: "sop-change-management",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Change Management",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_CLINICAL_EVALUATION]: {
    templateKey: TEMPLATE_ID.SOP_CLINICAL_EVALUATION,
    id: "sop-clinical-evaluation",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Clinical Evaluation",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_DEPLOYMENT]: {
    templateKey: TEMPLATE_ID.SOP_DEPLOYMENT,
    id: "sop-deployment",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Deployment",
    elements: [],
    isOrgDocument: true,
  },

  [TEMPLATE_ID.SOP_FEEDBACK_MANAGEMENT]: {
    templateKey: TEMPLATE_ID.SOP_FEEDBACK_MANAGEMENT,
    id: "sop-feedback-management",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Feedback Management",
    elements: [],
    isOrgDocument: true,
  },

  [TEMPLATE_ID.SOP_INTEGRATED_SOFTWARE_DEVELOPMENT]: {
    templateKey: TEMPLATE_ID.SOP_INTEGRATED_SOFTWARE_DEVELOPMENT,
    id: "sop-integrated-software-development",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Integrated Software Development",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_INTERNAL_AUDIT]: {
    templateKey: TEMPLATE_ID.SOP_INTERNAL_AUDIT,
    id: "sop-internal-audit",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Internal Audit",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_MANAGEMENT_REVIEW]: {
    templateKey: TEMPLATE_ID.SOP_MANAGEMENT_REVIEW,
    id: "sop-management-review",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Management Review",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_POST_MARKET_SURVEILLANCE]: {
    templateKey: TEMPLATE_ID.SOP_POST_MARKET_SURVEILLANCE,
    id: "sop-post-market-surveillance",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Post Market Surveillance",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_PRODUCT_REGISTRATION_AND_CERTIFICATION]: {
    templateKey: TEMPLATE_ID.SOP_PRODUCT_REGISTRATION_AND_CERTIFICATION,
    id: "sop-product-registration-and-certification",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Product Registration and Certification",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_PURCHASING]: {
    templateKey: TEMPLATE_ID.SOP_PURCHASING,
    id: "sop-purchasing",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Purchasing",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_RISK_MANAGEMENT]: {
    templateKey: TEMPLATE_ID.SOP_RISK_MANAGEMENT,
    id: "sop-risk-management",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Risk Management",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_SALES]: {
    templateKey: TEMPLATE_ID.SOP_SALES,
    id: "sop-sales",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Sales",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_SOFTWARE_PROBLEM_RESOLUTION]: {
    templateKey: TEMPLATE_ID.SOP_SOFTWARE_PROBLEM_RESOLUTION,
    id: "sop-software-problem-resolution",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Software Problem Resolution",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_SOFTWARE_VALIDATION]: {
    templateKey: TEMPLATE_ID.SOP_SOFTWARE_VALIDATION,
    id: "sop-software-validation",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Software Validation",
    elements: [],
    isOrgDocument: true,
  },

  [TEMPLATE_ID.SOP_VIGILANCE]: {
    templateKey: TEMPLATE_ID.SOP_VIGILANCE,
    id: "sop-vigilance",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Vigilance",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOUP_LIST]: {
    templateKey: TEMPLATE_ID.SOUP_LIST,
    id: "soup-list",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Soup List",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SUPPLIER_CHECKLIST]: {
    templateKey: TEMPLATE_ID.SUPPLIER_CHECKLIST,
    id: "supplier-checklist",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Supplier Checklist",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.TECHNICAL_FILE]: {
    templateKey: TEMPLATE_ID.TECHNICAL_FILE,
    id: "technical-file",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Technical File",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.UDI_CREATION]: {
    templateKey: TEMPLATE_ID.UDI_CREATION,
    id: "udi-creation",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "UDI Creation",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.USABILITY_EVALUATION_PLAN]: {
    templateKey: TEMPLATE_ID.USABILITY_EVALUATION_PLAN,
    id: "usability-evaluation-plan",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Usability Evaluation Plan",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.USABILITY_EVALUATION_PROTOCOL]: {
    templateKey: TEMPLATE_ID.USABILITY_EVALUATION_PROTOCOL,
    id: "usability-evaluation-protocol",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Usability Evaluation Protocol",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.USABILITY_EVALUATION_REPORT]: {
    templateKey: TEMPLATE_ID.USABILITY_EVALUATION_REPORT,
    id: "usability-evaluation-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Usability Evaluation Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.USER_MANUAL]: {
    templateKey: TEMPLATE_ID.USER_MANUAL,
    id: "user-manual",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "User Manual",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.USER_NEEDS_CHECKLIST]: {
    templateKey: TEMPLATE_ID.USER_NEEDS_CHECKLIST,
    id: "user-needs-checklist",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "User Needs Checklist",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.USER_NEEDS_LIST]: {
    id: TEMPLATE_ID.USER_NEEDS_LIST,
    templateKey: TEMPLATE_ID.USER_NEEDS_LIST,
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.PD,
    regulatoryType: RegulatoryTypeEnum.LST,
    defaultTitle: "User Needs List",
    dependencies: ["use-environment"],
    elements: [
      {
        id: "user-needs" as DocumentDataKey,
        context: ["use-environment"] as DocumentDataKey[],
        prompt: true,
        required: true,
        element: {
          id: "user-needs" as DocumentDataKey,
          type: "table" as const,
          options: {
            label: "What are the user needs for your product?",
            columns: [
              { name: "User Need ID", options: { flex: 0 } },
              { name: "Type of User Need", options: { flex: 0 } },
              { name: "Category", options: { flex: 0 } },
              { name: "Description", options: { multiline: true } },
            ],
            helperText: "Test helper text",
          },
        },
      },
      {
        id: "expected-lifetime" as DocumentDataKey,
        prompt: false,
        required: true,
        element: {
          id: "expected-lifetime" as DocumentDataKey,
          type: "textField" as const,
          options: {
            label: "What is the expected lifetime of your device?",
            type: "number" as const,
            helperText: "Test helper text",
          },
        },
      },
      {
        id: "sterilization-process" as DocumentDataKey,
        visibilityCondition: (_: any, device: any) =>
          hasSterileComponent(device),
        prompt: true,
        required: true,
        element: {
          id: "sterilization-process" as DocumentDataKey,
          type: "textField" as const,
          options: {
            label: "What type of sterilization process do you use?",
          },
        },
      },
      {
        id: "shelf-life" as DocumentDataKey,
        visibilityCondition: (_: any, device: any) => hasHardware(device),
        prompt: false,
        required: true,
        element: {
          id: "shelf-life" as DocumentDataKey,
          type: "textField" as const,
          options: {
            type: "number" as const,
            label: "What is your product's expected shelf-life?",
            helperText: "A round number in years.",
          },
        },
      },
    ],
  },
  [TEMPLATE_ID.SOP_DESIGN_CONTROL]: {
    templateKey: TEMPLATE_ID.SOP_DESIGN_CONTROL,
    id: "sop-design-control",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Design Control",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_NONCONFORMANCE]: {
    templateKey: TEMPLATE_ID.SOP_NONCONFORMANCE,
    id: "sop-nonconformance",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Nonconformance",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_USABILITY_ENGINEERING]: {
    templateKey: TEMPLATE_ID.SOP_USABILITY_ENGINEERING,
    id: "sop-usability-engineering",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Usability Engineering",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_CYBERSECURITY]: {
    templateKey: TEMPLATE_ID.SOP_CYBERSECURITY,
    id: "sop-cybersecurity",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Cybersecurity",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_STATISTICAL_METHODS]: {
    templateKey: TEMPLATE_ID.SOP_STATISTICAL_METHODS,
    id: "sop-statistical-methods",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Statistical Methods",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_MANUFACTURING]: {
    templateKey: TEMPLATE_ID.SOP_MANUFACTURING,
    id: "sop-manufacturing",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Manufacturing",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SYSTEM_REQUIREMENTS_LIST]: {
    templateKey: TEMPLATE_ID.SYSTEM_REQUIREMENTS_LIST,
    id: "system-requirements-list",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "System Requirements List",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SUBSYSTEM_REQUIREMENTS_LIST]: {
    templateKey: TEMPLATE_ID.SUBSYSTEM_REQUIREMENTS_LIST,
    id: "subsystem-requirements-list",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Subsystem Requirements List",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.HARDWARE_DESIGN_AND_DEVELOPMENT_PLAN]: {
    templateKey: TEMPLATE_ID.HARDWARE_DESIGN_AND_DEVELOPMENT_PLAN,
    id: "hardware-design-and-development-plan",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Hardware Design and Development Plan",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.MECHANICAL_DESIGN]: {
    templateKey: TEMPLATE_ID.MECHANICAL_DESIGN,
    id: "mechanical-design",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Mechanical Design",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.ELECTRICAL_DESIGN]: {
    templateKey: TEMPLATE_ID.ELECTRICAL_DESIGN,
    id: "electrical-design",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Electrical Design",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.FIRMWARE_DESIGN]: {
    templateKey: TEMPLATE_ID.FIRMWARE_DESIGN,
    id: "firmware-design",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Firmware Design",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.INTERFACE_DESIGN]: {
    templateKey: TEMPLATE_ID.INTERFACE_DESIGN,
    id: "interface-design",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Interface Design",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.PACKAGING_AND_SHELF_LIFE_DESIGN]: {
    templateKey: TEMPLATE_ID.PACKAGING_AND_SHELF_LIFE_DESIGN,
    id: "packaging-and-shelf-life-design",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Packaging and Shelf Life Design",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.DESIGN_REVIEW_CHECKLIST]: {
    templateKey: TEMPLATE_ID.DESIGN_REVIEW_CHECKLIST,
    id: "design-review-checklist",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Design Review Checklist",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.MANUFACTURING_VALIDATION_PLAN_AND_PROTOCOL]: {
    templateKey: TEMPLATE_ID.MANUFACTURING_VALIDATION_PLAN_AND_PROTOCOL,
    id: "manufacturing-validation-plan-and-protocol",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Manufacturing Validation Plan and Protocol",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.MANUFACTURING_VALIDATION_REPORT]: {
    templateKey: TEMPLATE_ID.MANUFACTURING_VALIDATION_REPORT,
    id: "manufacturing-validation-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Manufacturing Validation Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.DESIGN_TRANSFER_PLAN]: {
    templateKey: TEMPLATE_ID.DESIGN_TRANSFER_PLAN,
    id: "design-transfer-plan",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Design Transfer Plan",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.DESIGN_TRANSFER_REPORT]: {
    templateKey: TEMPLATE_ID.DESIGN_TRANSFER_REPORT,
    id: "design-transfer-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Design Transfer Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.VERIFICATION_AND_VALIDATION_PLAN]: {
    templateKey: TEMPLATE_ID.VERIFICATION_AND_VALIDATION_PLAN,
    id: "verification-and-validation-plan",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Verification and Validation Plan",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.BIOCOMPATIBILITY_EVALUATION_PLAN]: {
    templateKey: TEMPLATE_ID.BIOCOMPATIBILITY_EVALUATION_PLAN,
    id: "biocompatibility-evaluation-plan",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Biocompatibility Evaluation Plan",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.ELECTRICAL_VERIFICATION_PROTOCOL]: {
    templateKey: TEMPLATE_ID.ELECTRICAL_VERIFICATION_PROTOCOL,
    id: "electrical-verification-protocol",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Electrical Verification Protocol",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.PACKAGING_AND_SHELF_LIFE_VERIFICATION_PROTOCOL]: {
    templateKey: TEMPLATE_ID.PACKAGING_AND_SHELF_LIFE_VERIFICATION_PROTOCOL,
    id: "packaging-and-shelf-life-verification-protocol",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Packaging and Shelf Life Verification Protocol",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.PERFORMANCE_AND_BENCH_VERIFICATION_PROTOCOL]: {
    templateKey: TEMPLATE_ID.PERFORMANCE_AND_BENCH_VERIFICATION_PROTOCOL,
    id: "performance-and-bench-verification-protocol",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Performance and Bench Verification Protocol",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.PERFORMANCE_AND_BENCH_VALIDATION_PROTOCOL]: {
    templateKey: TEMPLATE_ID.PERFORMANCE_AND_BENCH_VALIDATION_PROTOCOL,
    id: "performance-and-bench-validation-protocol",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Performance and Bench Validation Protocol",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.BIOCOMPATIBILITY_EVALUATION_REPORT]: {
    templateKey: TEMPLATE_ID.BIOCOMPATIBILITY_EVALUATION_REPORT,
    id: "biocompatibility-evaluation-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Biocompatibility Evaluation Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.ELECTRICAL_VERIFICATION_REPORT]: {
    templateKey: TEMPLATE_ID.ELECTRICAL_VERIFICATION_REPORT,
    id: "electrical-verification-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Electrical Verification Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.PACKAGING_AND_SHELF_LIFE_VERIFICATION_REPORT]: {
    templateKey: TEMPLATE_ID.PACKAGING_AND_SHELF_LIFE_VERIFICATION_REPORT,
    id: "packaging-and-shelf-life-verification-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Packaging and Shelf Life Verification Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.PERFORMANCE_AND_BENCH_VERIFICATION_AND_VALIDATION_REPORT]: {
    templateKey:
      TEMPLATE_ID.PERFORMANCE_AND_BENCH_VERIFICATION_AND_VALIDATION_REPORT,
    id: "performance-and-bench-verification-and-validation-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Performance and Bench Verification and Validation Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.VERIFICATION_AND_VALIDATION_REPORT]: {
    templateKey: TEMPLATE_ID.VERIFICATION_AND_VALIDATION_REPORT,
    id: "verification-and-validation-report",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Verification and Validation Report",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.TRACEABILITY_MATRIX]: {
    templateKey: TEMPLATE_ID.TRACEABILITY_MATRIX,
    id: "traceability-matrix",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Traceability Matrix",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.PRODUCT_RELEASE_CHECKLIST]: {
    templateKey: TEMPLATE_ID.PRODUCT_RELEASE_CHECKLIST,
    id: "product-release-checklist",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Product Release Checklist",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.COMPLAINT_LOG]: {
    templateKey: TEMPLATE_ID.COMPLAINT_LOG,
    id: "complaint-log",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Complaint Log",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.PREDICATE_DEVICE]: {
    templateKey: TEMPLATE_ID.PREDICATE_DEVICE,
    id: "predicate-device",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Predicate Device",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.FDA_DEVICE_CLASSIFICATION]: {
    templateKey: TEMPLATE_ID.FDA_DEVICE_CLASSIFICATION,
    id: "fda-device-classification",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "FDA Device Classification",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.IVDR_DEVICE_CLASSIFICATION]: {
    templateKey: TEMPLATE_ID.IVDR_DEVICE_CLASSIFICATION,
    id: "ivdr-device-classification",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "IVDR Device Classification",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.FDA_REGISTRATION_AND_LISTING]: {
    templateKey: TEMPLATE_ID.FDA_REGISTRATION_AND_LISTING,
    id: "fda-registration-and-listing",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "FDA Registration and Listing",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.DESIGN_HISTORY_FILE]: {
    templateKey: TEMPLATE_ID.DESIGN_HISTORY_FILE,
    id: "design-history-file",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Design History File",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.FDA_510K_ESTAR]: {
    templateKey: TEMPLATE_ID.FDA_510K_ESTAR,
    id: "fda-510k-estar",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "FDA 510(k) ESTAR",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.FDA_PREDICATE_DEVICE]: {
    templateKey: TEMPLATE_ID.FDA_PREDICATE_DEVICE,
    id: "fda-predicate-device",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "FDA Predicate Device",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.TRAINING_LOG]: {
    templateKey: TEMPLATE_ID.TRAINING_LOG,
    id: "training-log",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Training Log",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.BIOLOGICAL_EVALUATION_PLAN]: {
    templateKey: TEMPLATE_ID.BIOLOGICAL_EVALUATION_PLAN,
    id: "biological-evaluation-plan",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Biological Evaluation Plan",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.USER_UPLOAD]: {
    templateKey: TEMPLATE_ID.USER_UPLOAD,
    id: "user-upload",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "User Upload",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOP_PERFORMANCE_EVALUATION]: {
    templateKey: TEMPLATE_ID.SOP_PERFORMANCE_EVALUATION,
    id: "sop-performance-evaluation",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "SOP Performance Evaluation",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SHELF_LIFE_VERIFICATION_GUIDE]: {
    templateKey: TEMPLATE_ID.SHELF_LIFE_VERIFICATION_GUIDE,
    id: "shelf-life-verification-guide",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Shelf Life Verification Guide",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.PERFORMANCE_AND_BENCH_VERIFICATION_GUIDE]: {
    templateKey: TEMPLATE_ID.PERFORMANCE_AND_BENCH_VERIFICATION_GUIDE,
    id: "performance-and-bench-verification-guide",
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    regulatoryType: RegulatoryTypeEnum.SOP,
    defaultTitle: "Performance and Bench Verification Guide",
    elements: [],
    isOrgDocument: true,
  },
  [TEMPLATE_ID.SOFTWARE_ARCHITECTURE]: {
    templateKey: TEMPLATE_ID.SOFTWARE_ARCHITECTURE,
    id: TEMPLATE_ID.SOFTWARE_ARCHITECTURE,
    defaultTitle: "Software Architecture",
    regulatoryType: RegulatoryTypeEnum.TCD,
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.SW,
    elements: [
      {
        id: "software-items-table",
        prompt: false,
        required: true,
        element: {
          id: "software-items-table",
          type: "table",
          options: {
            label:
              "Describe your software items and tools that make up your software.",
            default: `
              | Software Item | Description             | Programming Language (Version) | Framework (Version) |
              | ------------- | ----------------------- | ------------------------------ | ------------------- |
              | Backend       | Handles data processing | JavaScript (x.x.x)             | NodeJS (x.x.x)      |
              | Frontend      | Displays health data    | TypeScript (5.0.2)             | React (18.2.0)      |
              | Database      | Health data storage     | SQL (x.x.x)                    | PostgreSQL (x.x.x)  |
              `,
            columns: [
              { name: "Software Item" },
              { name: "Description" },
              { name: "Programming Language (Version)" },
              { name: "Framework (Version)" },
            ],
            helperText: `The software architecture you describe here should be detailed enough that the auditors understand the basics of how your software is organized and what its interdependencies are. Keep everything high level and include versions of technologies you use where possible. We recommend breaking your software architecture into the following:
  
                - Description: Write a sentence describing what the software item does in a basic sense
                - Examples of software items: Frontend(s), Backend(s), Database(s), Medical Algorithms
                - Examples of frameworks: React, JavaScript, FastAPI, NodeJS, PostgreSQL, etc.`,
          },
        },
      },
      {
        id: "software-connections-table",
        prompt: true,
        required: true,
        context: ["software-items-table"],
        element: {
          id: "software-connections-table",
          type: "table",
          options: {
            label:
              "Describe how the software is deployed and its connections, and hardware requirements.",
            columns: [
              { name: "Software Item" },
              { name: "Infrastructure" },
              { name: "Hardware Requirements" },
              { name: "Operating System" },
              { name: "Connections" },
            ],
            helperText: `The software connections provides an idea of how the software is deployed and interconnected. We also will add some basic hardware requirements for the software item. Below are some examples of the software connectivity descriptions.
  
              - Examples of infrastructure: AWS, Digital Ocean, Internal hardware
              - Hardware requirements: Put your minimum requirements for the software item(e.g. - 2 servers (4 GB memory, 2 CPU each), Wi-Fi connection,  iOS and Android Operating system)
              - Connections: List all software items connected to this one. For example if you are filling this out for your Frontend and it is connected to "Backend 1" and the "Database" you would write "Backend 1, Database". Easy as that.`,
          },
        },
      },
      {
        id: "software-architecture-diagram",
        prompt: false,
        context: ["software-connections-table"],
        required: true,
        element: {
          id: "software-architecture-diagram",
          type: "fileUpload",
          options: {
            label: "Upload a diagram of your software architecture.",
            helperText:
              "This diagram should be a high level overview of your software architecture. It should include all software items and their connections. It should also include any hardware requirements for your software items. Consolidate all diagrams into 1 file before uploading.",
            allowedFileTypes: "image/png,image/jpeg,image/jpg",
          },
        },
      },
      {
        id: "software-architecture-security",
        prompt: true,
        context: ["software-items-table", "software-connections-table"],
        required: true,
        element: {
          id: "software-architecture-security",
          type: "textField",
          options: {
            label: "Describe your software security and encryption practices.",
            helperText:
              "Please describe how data security and encryption are handled by your software, if applicable.",
            rows: 5,
          },
        },
      },
      {
        id: "software-architecture-backup",
        prompt: true,
        context: ["software-items-table", "software-connections-table"],
        required: true,
        element: {
          id: "software-architecture-backup",
          type: "textField",
          options: {
            label: "Describe your software backup methods and details.",
            helperText:
              "Please describe how data backup is handled by your software, if applicable. Include backup methods and details like location and frequency.",
            rows: 5,
          },
        },
      },
      {
        id: "minimum-requirements",
        prompt: true,
        context: ["intended-use", "software-items-table"],
        required: true,
        element: {
          id: "minimum-requirements",
          type: "textField",
          options: {
            label:
              "Provide the minimum user system requirements for the computer that your software will run on.",
            rows: 5,
            helperText:
              "This should include things like minimum hardware requirements (CPU, RAM, etc.), minimum software requirements (browser version, operating systems version, etc.), and any other requirements that are necessary for the software to run (Bluetooth version, etc.).",
          },
        },
      },
    ],
  },
  [TEMPLATE_ID.SOFTWARE_REQUIREMENTS_LIST]: {
    id: TEMPLATE_ID.SOFTWARE_REQUIREMENTS_LIST,
    templateKey: TEMPLATE_ID.SOFTWARE_REQUIREMENTS_LIST,
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.SW,
    regulatoryType: RegulatoryTypeEnum.LST,
    defaultTitle: "Software Requirements List",
    deprecatedByTemplateKey: TEMPLATE_ID.SOFTWARE_REQUIREMENTS_REVIEW_CHECKLIST,
    elements: [
      {
        id: "software-requirements",
        prompt: true,
        context: ["user-needs", "software-items-table"],
        required: true,
        element: {
          id: "software-requirements",
          type: "table",
          options: {
            label: "What are your software requirements?",
            helperText:
              "Software requirements define the key functions of your device as required. Think of them as user stories but more generalized to the function of the actual software. We will use your User Needs and Risk Assessment as inputs for your software requirements as they form the framework for what should be designed into the device.",
            // | Software ID | Software Item | Category | Software Requirement |
            columns: [
              {
                name: "Software ID",
                options: { flex: 0 },
              },
              {
                name: "Software Item",
                options: { flex: 0 },
              },
              {
                name: "Category",
                options: { flex: 0 },
              },
              {
                name: "Software Requirement",
              },
            ],
          },
        },
      },
      {
        id: "risk-control-software-requirements",
        prompt: true,
        context: ["risk-controls", "software-items-table"],
        required: true,
        element: {
          id: "risk-control-software-requirements",
          type: "table",
          options: {
            label:
              "Provide software requirements that are based on your risk controls.",
            helperText:
              "During the risk assessment, we decided what risks may need additional risk controls to make the product safe enough for our users. Here, we want to implement software requirements for those risk controls so that we can build them into the product. Provide a software requirement for any risk controls or any software features that are to be implemented to help reduce risk to the patients. These should be worded the same way as the software requirements we created in the previous step.",
            // | Software ID | Software Item | Category | Software Requirement |
            columns: [
              {
                name: "Software ID",
                options: { flex: 0 },
              },
              {
                name: "Software Item",
                options: { flex: 0 },
              },
              {
                name: "Category",
                options: { flex: 0 },
              },
              {
                name: "Software Requirement",
              },
            ],
          },
        },
      },
    ],
  },
  [TEMPLATE_ID.SOFTWARE_ARCHITECTURE_CHECKLIST]: {
    templateKey: TEMPLATE_ID.SOFTWARE_ARCHITECTURE_CHECKLIST,
    id: TEMPLATE_ID.SOFTWARE_ARCHITECTURE_CHECKLIST,
    defaultTitle: "Software Architecture Checklist",
    regulatoryType: RegulatoryTypeEnum.RCD,
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.SW,
    deprecatedByTemplateKey: TEMPLATE_ID.DESIGN_REVIEW_CHECKLIST,
    dependencies: ["software-architecture-checklist-table"],
    elements: [
      {
        id: "software-architecture-checklist-comments",
        prompt: false,
        required: false,
        element: {
          id: "software-architecture-checklist-comments",
          type: "textField",
          options: {
            label:
              "Provide any comments related to the software architecture review that might be relevant.",
            rows: 5,
            helperText:
              "Cover any issues that may have arisen in the review and justify why they are still acceptable or maybe even unacceptable. Anything you think might be important to note.",
          },
        },
      },
      {
        id: "software-architecture-checklist-results",
        prompt: false,
        required: true,
        element: {
          id: "software-architecture-checklist-results",
          type: "select",
          options: {
            label: "Did the software architecture pass review?",
            options: [
              "Software Architecture acceptable",
              "Software Architecture not acceptable",
              "Software Architecture acceptable with obligations",
            ],
          },
        },
      },
      {
        id: "software-architecture-checklist-obligations",
        prompt: false,
        required: false,
        element: {
          id: "software-architecture-checklist-obligations",
          type: "textField",
          options: {
            label: "What obligations did the software architecture pass with?",
            rows: 5,
            helperText:
              "ONLY FILL THIS OUT IF THE REVIEW PASSED WITH OBLIGATIONS. This can be requirements for future resources or the eventual separation of software items. Any minor changes that should be included as a result of review.",
          },
        },
      },
    ],
  },
  [TEMPLATE_ID.MDR_DEVICE_CLASSIFICATION]: {
    id: TEMPLATE_ID.MDR_DEVICE_CLASSIFICATION,
    templateKey: TEMPLATE_ID.MDR_DEVICE_CLASSIFICATION,
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.TD,
    regulatoryType: RegulatoryTypeEnum.TCD,
    defaultTitle: "MDR Classification Document",
    elements: [
      {
        id: "duration-of-use",
        prompt: true,
        required: true,
        element: {
          id: "duration-of-use",
          type: "select",
          options: {
            label: "What is the duration of use for your product?",

            options: [
              "Transient - less than 60 minutes.",
              "Short term - not less than 60 minutes and not more than 30 days.",
              "Long term - more than 30 days.",
            ],
            default: "Transient - less than 60 minutes.",
            helperText:
              "Provide your product duration of use here. It can include:\nTransient - Normally intended for continuous use for less than 60 minutes. Short term - Normally intended for continuous use for not less than 60 minutes and not more than 30 days. Long term - Normally intended for continuous use for more than 30 days.",
          },
        },
      },
      {
        id: "device-classification",
        prompt: true,
        context: ["intended-use", "duration-of-use"],
        required: true,
        element: {
          id: "device-classification",
          type: "select",
          options: {
            label:
              "What is the device classification based on Annex VIII of the EU MDR 2017/745?",
            options: ["Class I", "Class IIa", "Class IIb", "Class III"],
            default: "Class IIa",
            helperText: "",
          },
        },
      },
      {
        id: "device-classification-justification",
        prompt: true,
        context: ["intended-use", "device-classification", "duration-of-use"],
        required: true,
        element: {
          id: "device-classification-justification",
          type: "textField",
          options: {
            label: "What is your justification for your device classification?",
            rows: 5,
            helperText:
              "The classification of your device should be according to the rules of Annex VIII of the EU MDR 2017/745 rules. Provide only those rules that are applicable to you in your device classification.",
          },
        },
      },
    ],
  },
  [TEMPLATE_ID.LIST_OF_QUALIFIED_SUPPLIERS]: {
    id: TEMPLATE_ID.LIST_OF_QUALIFIED_SUPPLIERS,
    templateKey: TEMPLATE_ID.LIST_OF_QUALIFIED_SUPPLIERS,
    defaultTitle: "Approved Supplier List",
    regulatoryType: RegulatoryTypeEnum.LST,
    hidden: false,
    isOrgDocument: true,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.CP,
    docReferences: [TEMPLATE_ID.SOP_PURCHASING],
    elements: [
      {
        id: "list-of-qualified-suppliers",
        prompt: false,
        required: true,
        element: {
          id: "list-of-qualified-suppliers",
          type: "answerItemsElement",
          options: {
            typeId: "QS",
            outputTransformer: ({ answer, element }) => {
              const value = answer;
              const fields = element.options.fields;
              const rowLabels = element.options.fields.map((f) => f.label);

              // Generate markdown tables for each item in the value
              const markdownTables = value
                .filter((item: AnswerItem) => item.archived === false)
                .map((item: AnswerItem) => {
                  let table = "| Category | Information |\n";
                  table += "| -------- | ----------- |\n";
                  rowLabels.forEach((label, index) => {
                    const fieldKey = fields[index].fieldKey;
                    table += `| ${label} | ${item?.fields?.[fieldKey] || ""} |\n`;
                  });
                  return table;
                })
                .join("\n");

              return markdownTables;
            },
            label: "Provide the details for your critical suppliers.",
            fields: [
              {
                label: "Supplier Name",
                fieldKey: "name",
                element: "textField",
              },
              {
                label: "Supplier contact information",
                fieldKey: "contact_information",
                element: "textField",
              },
              {
                label: "Supplier / Product Criticality",
                fieldKey: "criticality",
                element: "select",
                options: [
                  { value: "Critical", label: "Critical" },
                  { value: "Non-Critical", label: "Non-Critical" },
                ],
              },
              {
                label: "Criticality Rationale",
                fieldKey: "criticality_rationale",
                element: "select",
                options: [
                  {
                    value:
                      "The purchase product or services could directly impact the performance OR safety of the medical device(s).",
                    label:
                      "The purchase product or services could directly impact the performance OR safety of the medical device(s).",
                  },
                  {
                    value:
                      "The purchased product or services could directly impact our ability to maintain product or company regulatory compliance.",
                    label:
                      "The purchased product or services could directly impact our ability to maintain product or company regulatory compliance.",
                  },
                  {
                    value:
                      "It is not possible for our medical device to be developed or produced without the product or service AND there are no alternative or equivalent suppliers of that product or service available.",
                    label:
                      "It is not possible for our medical device to be developed or produced without the product or service AND there are no alternative or equivalent suppliers of that product or service available.",
                  },
                  { value: "Non-critical", label: "Non-critical" },
                ],
              },
              {
                label: "Description of Service(s) / Product(s)",
                fieldKey: "purchase_description",
                element: "textField",
              },
              {
                label: "Supplier Status",
                fieldKey: "status",
                element: "select",
                alwaysShown: true,
                options: [
                  { value: "Approved", label: "Approved" },
                  {
                    value: "Approved with Monitoring",
                    label: "Approved with Monitoring",
                  },
                  { value: "Blocked", label: "Blocked" },
                ],
              },

              {
                label: "Average Score",
                fieldKey: "average_score",
                element: "select",
                options: [
                  { value: "Score >2", label: "Score >2" },
                  { value: "Score 1-2", label: "Score 1-2" },
                  { value: "Score <1", label: "Score <1" },
                ],
              },
              {
                label: "Date of Last Evaluation",
                fieldKey: "date_of_last_evaluation",
                element: "dateInput",
                style: {
                  width: "calc(33% - 5px)",
                  minWidth: "calc(33% - 5px)",
                  flex: 1,
                },
                alwaysShown: true,
              },
              {
                label: "Non-Disclosure Agreement (NDA):",
                fieldKey: "non_disclosure_agreement",
                element: "radioButton",
                style: { width: "48%" },
                options: [
                  { value: "Yes", label: "Yes" },
                  { value: "No", label: "No" },
                ],
              },
              {
                label: "Supplier Certification:",
                fieldKey: "certification",
                element: "textField",
              },
              {
                label: "Quality Assurance Agreement (QAA):",
                fieldKey: "quality_assurance_agreement",
                element: "radioButton",
                style: { width: "49%" },
                options: [
                  { value: "Yes", label: "Yes" },
                  { value: "No", label: "No" },
                ],
              },
              {
                label: "Supplier Auditing:",
                fieldKey: "auditing",
                element: "radioButton",
                style: { width: "49%" },
                options: [
                  { value: "Yes", label: "Yes" },
                  { value: "No", label: "No" },
                ],
              },
              {
                label: "Other Monitoring Measures:",
                fieldKey: "other_monitoring_measures",
                element: "textField",
              },
            ],
            helperText: `Add in your suppliers here and perform a supplier evaluation on each using the Supplier Qualification Checklist. After the evaluation, add in the information related to the date of evaluation, their evaluation score, and their status. For the table:

- Supplier Contact Information: Provide the name and email address of the contact or how you typically contact the supplier (can also be through a web portal, chat, etc.)
- Description of Service(s) / Product(s): Describe the products or services you are purchasing from this supplier individually. Keep in mind we need to determine if any of these are "critical" in the step below.
- Supplier / Product Criticality: If the supplier is providing a critical product, they are critical as well. Select Yes or No if the product is critical. Refer to the Criticality Rationale for what designates a critical product.
- Criticality Rationale: Select any of the options. If it is not critical, select none of the above options.
- Date of last evaluation: The date of the last evaluation of the supplier.
- Supplier status: Approved, approved with monitoring, or blocked.
- Supplier Evaluation Overall Score : The average score from the evaluation of the above ratings.
- Supplier certification: Describe what certifications the supplier has, if any.
- Additional monitoring measures: Describe any additional monitoring measures in place for this supplier, if applicable.
- Non-disclosure agreement (NDA): Whether the supplier has signed an NDA with you.
- Quality assurance agreement (QAA): Whether the supplier has signed a QAA with you.
- Supplier auditing: Whether the supplier is subject to auditing.
`,
          },
        },
      },
    ],
  },
  [TEMPLATE_ID.RISK_ASSESSMENT]: {
    id: TEMPLATE_ID.RISK_ASSESSMENT,
    templateKey: TEMPLATE_ID.RISK_ASSESSMENT,
    hidden: false,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.TD,
    regulatoryType: RegulatoryTypeEnum.TCD,
    defaultTitle: "Risk Assessment",
    elements: [
      {
        id: "software-safety-classification",
        prompt: true,
        required: true,
        element: {
          id: "software-safety-classification",
          type: "select",
          options: {
            label: "What is the software safety classification of your device?",
            helperText: `The safety classification is based on ISO 62304:2006 and is dependent on the type of risk your software will present the patient with if it fails.  The software safety classifications are the following: 
              Class A: No injury or damage to health is possible
              Class B: Non-serious injury is possible
              Class C: Death or serious Injury is possible`,
            options: [
              "Class A: No injury or damage to health is possible",
              "Class B: Non-serious injury is possible",
              "Class C: Death or serious Injury is possible",
            ],
          },
        },
        visibilityCondition: (_, device) => {
          return hasSoftware(device);
        },
      },
      {
        id: "risk-analysis",
        prompt: true,
        context: [
          "intended-use",
          "user-profile",
          "patient-population",
          "user-needs",
          "risk-acceptance-matrix",
          "software-safety-classification",
        ],
        required: true,
        element: {
          id: "risk-analysis",
          type: "table",

          options: {
            columns: [
              {
                name: "Risk ID",
                options: { flex: 0 },
              },
              {
                name: "Risk Type",
                options: { flex: 0 },
              },
              {
                name: "Hazard",
              },
              {
                name: "p1",
                options: { flex: 0 },
              },
              {
                name: "Hazardous Situation",
              },
              {
                name: "p2",
                options: { flex: 0 },
              },
              {
                name: "Harm",
              },
              {
                name: "p1*p2",
                options: { flex: 0 },
              },
              {
                name: "P",
                options: { flex: 0 },
              },
              {
                name: "S",
                options: { flex: 0 },
              },
              {
                name: "Risk Acceptability",
              },
            ],
            idColumn: "Risk ID",
            label:
              "Check the risk analysis so that the risks and probabilities are accurate for your device.",
            helperText: `The risk analysis tries to walk through any foreseeable risks of that may occur with the device given its intended use and patient and user populations. These risks should be ones that you can imagine happening with a typical user of the device. Risks follow the typical pathway from Hazard -> Hazardous Situation -> Harm. Between hazard and hazardous situation you can imagine there being a sequence events that can occur. That is why we include a probability there as p1. Between the hazardous situation and the harm, there is another sequence of events that can occur which begets yet another probability. We multiple these two probabilities in the "p1*p2" column to get our final probability of that risk leading to harm for our users or patients.
  
              **Please check the math on the final p1*p2 calculations to ensure they are correct, our AI sometimes forgets its arithmetic**.
              After the risk probability is calculated, we will determine the final P value based on the "Probability of Occurrence Table" and determine the severity of the harm based on the "Severity of Harm Table". Finally, we use our risk matrix we created earlier to determine if our risk is acceptable, reduce risk AFAP, or unacceptable. This is captured in the final column. Double check that these values are correct, amend any risks that could be improved, and add any risks that were forgotten.`,
          },
        },
      },

      {
        id: "risk-controls",
        prompt: true,
        context: ["intended-use", "risk-analysis", "risk-acceptance-matrix"],
        required: true,
        element: {
          id: "risk-controls",
          type: "table",
          options: {
            // Adjust to
            // columns: {
            //   name: string;
            //   options?: {
            //     flex?: number;
            //   };
            // }[];

            columns: [
              { name: "Risk ID", options: { flex: 0 } },
              { name: "Risk Control ID", options: { flex: 0 } },
              { name: "Risk Control Type" },
              { name: "Risk Control Methods" },
              { name: "Risk Control Verification" },
              { name: "Risk Control Effective" },
              { name: "New Probability" },
              { name: "New Severity" },
              { name: "New Risk Acceptability" },
            ],
            idColumn: "Risk ID",

            label:
              "Check that the risk controls suggested in the table are the method you would prefer to reduce risk.",
            helperText: `Risk controls are ways of reducing risks so that the final risk of harm to the patient and/or user is acceptable. There are 3 ways of reducing risk: implementing a safer design, adding protective measures (like alarms, alerts, protective sleeves, etc.), or adding information for safety that the user or patient can read. These are in the "Risk Control Type" column and should not be changed. The other information like the "risk control methods" can be changed to match what you would like to do to reduce risk. Only those risks in the"reduce risk AFAP" or "unacceptable" range really need risk controls but you can implement ones for other risks as well if you would like. Finally, a new Probability of Occurrence and new Severity of Harm are calculated based on the expected results of the risk controls and we can determine our new risk acceptability using the risk matrix we created earlier (and used during the risk analysis last step). We will confirm if these risk controls REALLY worked in the later stages (that is what risk control verification means). For now, we can estimate what we think will happen.`,
          },
        },
      },
    ],
  },
};

export const mockSoftwareDevice: Device = {
  id: "device-1",
  name: "Device 1",
  description: "Device 1 description",
  isMDR: true,
  isFDA: false,
  isIVDR: false,
  isDemoDevice: false,
  components: [
    {
      id: "component-1",
      name: "Component 1",
      description: "Component 1 description",
      type: ComponentType.SOFTWARE,
      deviceId: "device-1",
      configuration: [
        {
          id: "component-1",
          componentId: "component-1",
          type: ComponentConfigurationType.HAS_AI,
          value: false,
        },
      ],
      subComponents: [],
    },
  ],
  createdBy: "user-1",
  createdAt: new Date(),
  updatedAt: new Date(),
};

export const mockHardwareDevice: Device = {
  id: "device-1",
  name: "Device 1",
  description: "Device 1 description",
  isMDR: true,
  isFDA: false,
  isIVDR: false,
  isDemoDevice: false,
  components: [
    {
      id: "component-1",
      name: "Component 1",
      description: "Component 1 description",
      type: ComponentType.HARDWARE,
      configuration: [],
      subComponents: [],
      deviceId: "device-1",
    },
  ],
  createdBy: "user-1",
  createdAt: new Date(),
  updatedAt: new Date(),
};

export const mockHardwareDeviceWithSterile: Device = {
  ...mockHardwareDevice,
  components: [
    ...mockHardwareDevice.components,
    {
      ...mockHardwareDevice.components[0],
      configuration: [
        {
          id: "component-1",
          componentId: "component-1",
          type: ComponentConfigurationType.IS_STERILIZATION_INTENDED,
          value: "true",
        },
      ],
    },
  ],
};

const userId = "user-1";
export const mockOrgId = "1";

export const mockOrganization: Organization = {
  id: mockOrgId,
  name: "Organization 1",
  createdAt: new Date(),
  updatedAt: new Date(),
  subscription: null,
};

const membership: OrganizationMemberWithOrganization = {
  id: "1",
  organizationId: mockOrgId,
  homeOrg: true,
  createdAt: new Date(),
  updatedAt: new Date(),
  userId,
  organization: mockOrganization,
};

export const subscription: Subscription = {
  id: "1",
  organizationId: "1",
  status: "active",
  stripeSubscriptionId: "1",
  subscriptionItems: [
    {
      id: "1",
      subscriptionId: "1",
      productId: "1",
      productQuantity: 1,
      interval: BillingInterval.MONTHLY,
      product: {
        id: "1",
        name: "Premium+",
        stripeProductId: window._env_.PREMIUM_PLUS_STRIPE_PRODUCT_ID,
        entitlements: [
          {
            id: "1",
            type: EntitlementType.CONSULTING,
            value_bool: true,
            value_int: null,
          },
          {
            id: "2",
            type: EntitlementType.DEVICE_LIMIT,
            value_bool: null,
            value_int: 1,
          },
          {
            id: "3",
            type: EntitlementType.ALL_DOCUMENTS_UNLOCKED,
            value_bool: true,
            value_int: null,
          },
        ],
      },
    },
  ],
};

export const mockFreeUser: User = {
  id: userId,
  authId: "auth-1",
  firstName: "User",
  lastName: "1",
  email: "user1@example.com",
  createdAt: new Date(),
  updatedAt: new Date(),
  organizationMemberships: [membership],
  userUiState: {
    welcomeSeen: true,
    supportTooltipSeen: true,
    nextTodoCardTooltipSeen: true,
    approvalTooltipSeen: true,
    demoCompleteModalSeen: true,
    id: "1",
    createdAt: new Date(),
    updatedAt: new Date(),
    userId,
  },
};

export const mockPremiumUser: User = {
  ...mockFreeUser,
  organizationMemberships: [
    {
      ...mockFreeUser.organizationMemberships[0],
      organization: {
        ...mockFreeUser.organizationMemberships[0].organization,
        subscription,
      },
    },
  ],
};

export const mockDocumentVersion: DocumentVersion = {
  id: "123",
  documentId: "123",
  readyForApproval: false,
  documentVersionApprover: [],
  revision: null,
  createdBy: userId,
  createdAt: new Date(),
  updatedAt: new Date(),
  snapshot: null,
  revisionSummary: "",
  answers: [],
  suggestions: [],
  fileId: null,
  file: null,
};

export const mockFile: File = {
  id: "123",
  organizationId: mockOrgId,
  path: "123",
  createdAt: new Date(),
  createdBy: userId,
  encoding: "utf-8",
  mimeType: "image/png",
  filename: "test.png",
};

export const mockDocument: Document = {
  id: "123",
  templateKey: TEMPLATE_ID.CLINICAL_EVALUATION_REPORT,
  deviceId: "device-1",
  createdBy: userId,
  createdAt: new Date(),
  updatedAt: new Date(),
  versions: [mockDocumentVersion],
  title: "Test Document",
  regulatoryType: RegulatoryTypeEnum.RCD,
  regulatoryFunctionalGroup: RegulatoryFunctionalGroup.UNGROUPED,
};

export const mockDocumentAnswer: DocumentAnswer = {
  element: "expected-lifetime",
  answer: "correct",
  id: "123",
  createdAt: new Date(),
  documentVersionId: "123",
  createdBy: userId,
};

export const mockEntitlement: Entitlement = {
  id: "ent-1",
  type: EntitlementType.DEVICE_LIMIT,
  value_int: -1,
};

export const mockProduct: Product = {
  id: "prod-1",
  name: "Premium Flat",
  stripeProductId: window._env_.PREMIUM_STRIPE_PRODUCT_ID,
  entitlements: [mockEntitlement],
};

const subscriptionId = "sub-1";

export const mockSubscriptionItem: SubscriptionItem = {
  id: "item-1",
  productQuantity: 2,
  interval: BillingInterval.YEARLY,
  subscriptionId: subscriptionId,
  productId: mockProduct.id,
  product: mockProduct,
};

export const mockSubscription: Subscription = {
  id: subscriptionId,
  organizationId: mockOrganization.id,
  status: "active",
  stripeSubscriptionId: "stripe-sub-1",
  subscriptionItems: [mockSubscriptionItem],
};

export const mockAnswerItems = [
  {
    type: "QS",
    id: "qs-1",
    organizationId: "org-1",
    createdBy: "user-1",
    createdAt: new Date(),
    updatedAt: new Date(),
    archived: true, // This item should be skipped
    sequentialTypeNumber: 1,
    fields: {
      name: "Archived Supplier",
      purchase_description: "Test Purchase",
      status: "Approved",
    },
  },
  {
    type: "QS",
    id: "qs-2",
    organizationId: "org-1",
    createdBy: "user-1",
    createdAt: new Date(),
    updatedAt: new Date(),
    archived: false,
    sequentialTypeNumber: 2,
    fields: {
      name: "Active Supplier",
      purchase_description: "Test Purchase",
      status: "Approved",
    },
  },
];

export const mockVersionWithAnswerItems: DocumentVersion = {
  ...mockDocumentVersion,
  answers: [
    {
      ...mockDocumentAnswer,
      element: "list-of-qualified-suppliers",
      answerItems: mockAnswerItems,
    },
  ],
};

export const mockDocuments: Document[] = [
  {
    id: "doc-1",
    templateKey: TEMPLATE_ID.CLINICAL_EVALUATION_REPORT,
    deviceId: "device-1",
    createdBy: userId,
    createdAt: new Date(),
    updatedAt: new Date(),
    title: "Clinical Evaluation Report",
    regulatoryType: RegulatoryTypeEnum.TCD,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
    versions: [
      {
        ...mockDocumentVersion,
        id: "version-1",
        documentId: "doc-1",
      },
    ],
  },
  {
    id: "doc-2",
    templateKey: TEMPLATE_ID.BUG_FIX_REPORT,
    deviceId: "device-1",
    createdBy: userId,
    createdAt: new Date(),
    updatedAt: new Date(),
    title: "Bug Fix Report",
    regulatoryType: RegulatoryTypeEnum.RCD,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.SW,
    versions: [
      {
        ...mockDocumentVersion,
        id: "version-2",
        documentId: "doc-2",
      },
    ],
  },
  {
    id: "doc-7",
    templateKey: TEMPLATE_ID.SOP_CHANGE_MANAGEMENT,
    deviceId: "device-1",
    createdBy: "user-1",
    createdAt: new Date(),
    updatedAt: new Date(),
    versions: [mockDocumentVersion],
    title: "SOP Change Management",
    regulatoryType: RegulatoryTypeEnum.SOP,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.QA,
  },
  {
    id: "doc-8",
    templateKey: TEMPLATE_ID.CHECKLIST_SOFTWARE_RELEASE,
    deviceId: "device-1",
    createdBy: "user-1",
    createdAt: new Date(),
    updatedAt: new Date(),
    versions: [mockDocumentVersion],
    title: "Checklist Software Release",
    regulatoryType: RegulatoryTypeEnum.RCD,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.SW,
  },
  {
    id: "doc-10",
    templateKey: TEMPLATE_ID.SOP_PURCHASING,
    deviceId: "device-1",
    createdBy: "user-1",
    createdAt: new Date(),
    updatedAt: new Date(),
    versions: [mockDocumentVersion],
    title: "SOP Purchasing",
    regulatoryType: RegulatoryTypeEnum.SOP,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.CP,
  },
  {
    id: "doc-11",
    templateKey: TEMPLATE_ID.USER_UPLOAD,
    deviceId: "device-1",
    createdBy: "user-1",
    createdAt: new Date(),
    updatedAt: new Date(),
    title: "User File Upload",
    regulatoryType: null,
    regulatoryFunctionalGroup: RegulatoryFunctionalGroup.UNGROUPED,
    versions: [
      {
        ...mockDocumentVersion,
        documentId: "doc-11",
        fileId: mockFile.id,
        file: mockFile,
      },
    ],
  },
];

export const mockStep: TemplateElement = {
  id: "applied-clinical-trials-search-string",
  required: true,
  element: {
    type: "textField",
    id: "test-field",
    options: {
      label: "Test Field",
    },
  },
};

export const mockSuggestion: Suggestion = {
  applied: false,
  value: "",
  completed: false,
  isStatusMessage: false,
  statusMessageType: undefined,
  loading: false,
  error: false,
};

export const mockDocumentVersionWithAnswers: DocumentVersion = {
  id: "version-1",
  documentId: "doc-1",
  readyForApproval: false,
  documentVersionApprover: [],
  revision: 1,
  createdBy: "user-1",
  createdAt: new Date("2023-01-01"),
  updatedAt: new Date("2023-01-01"),
  snapshot: null,
  revisionSummary: "This is a revision summary",
  suggestions: [],
  answers: [
    {
      id: "device-name",
      answer: "Test Device",
      element: "device-name",
      documentVersionId: mockDocumentVersion.id,
      createdBy: "user-1",
      createdAt: new Date("2023-01-01"),
    },
    {
      id: "software-items-table",
      answer: "| Software Item | Description |\n| Test | Test |",
      element: "software-items-table",
      documentVersionId: mockDocumentVersion.id,
      createdBy: "user-1",
      createdAt: new Date("2023-01-01"),
    },
    {
      id: "software-connections-table",
      answer: "| Software Item | Infrastructure |\n| Test | Test |",
      element: "software-connections-table",
      documentVersionId: mockDocumentVersion.id,
      createdBy: "user-1",
      createdAt: new Date("2023-01-01"),
    },
    {
      id: "software-architecture-diagram",
      answerFileId: "file-123",
      element: "software-architecture-diagram",
      documentVersionId: mockDocumentVersion.id,
      createdBy: "user-1",
      createdAt: new Date("2023-01-01"),
    },
    {
      id: "software-architecture-security",
      answer: "Security details",
      element: "software-architecture-security",
      documentVersionId: mockDocumentVersion.id,
      createdBy: "user-1",
      createdAt: new Date("2023-01-01"),
    },
    {
      id: "software-architecture-backup",
      answer: "Backup details",
      element: "software-architecture-backup",
      documentVersionId: mockDocumentVersion.id,
      createdBy: "user-1",
      createdAt: new Date("2023-01-01"),
    },
    {
      id: "minimum-requirements",
      answer: "Minimum requirements details",
      element: "minimum-requirements",
      documentVersionId: mockDocumentVersion.id,
      createdBy: "user-1",
      createdAt: new Date("2023-01-01"),
    },
  ],
  fileId: null,
  file: null,
};

export const mockDevice: Device = {
  id: "test-device",
  name: "Test Device",
  createdAt: new Date(),
  updatedAt: new Date(),
  createdBy: userId,
  isFDA: false,
  isIVDR: false,
  isMDR: true,
  isDemoDevice: false,
  components: [],
  description: "Test Device",
};

export const mockSuggestionRequestBody: SuggestionRequestBody = {
  characteristics: [],
  device_name: "Test Device",
  device_description: "Bla bla bla",
  additional_context: [],
  context_files: [],
  components: [],
  step: {
    question: "What is the intended use of the device?",
    description: `Your intended use describes how your product functions at its core. What you described in your intended use statement MUST be defendable by data and will end up going into your instructions manual so it is important to phrase this statement carefully so you don't promise too much or too little.`,
    default_value: null,
    id: "intended-use",
    output_config: {},
    response_type: "textField",
  },
};

export const mockChangeSuggestionRequestBody: ChangeSuggestionRequestBody = {
  additional_context: [],
  change_information: "Bla bla bla",
  characteristics: [],
  components: [],
  context_files: [],
  device_description: "Bla bla bla",
  device_name: "Test Device",
  step: {
    question: "What is the intended use of the device?",
    description: `Your intended use describes how your product functions at its core. What you described in your intended use statement MUST be defendable by data and will end up going into your instructions manual so it is important to phrase this statement carefully so you don't promise too much or too little.`,
    default_value: null,
    id: "intended-use",
    output_config: {},
    response_type: "textField",
  },
};

export const mockAssistantProcess: AssistantProcess = {
  id: "test-id",
  organizationId: mockOrgId,
  deviceId: mockDevice.id,
  createdBy: userId,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  status: AssistantProcessStatus.IN_PROGRESS,
  state: {
    checkboxes: [],
    description: "",
    currentPath: "",
    changeType: "",
    changes: [],
    dataKeysToUpdate: [],
  },
};

export const mockGetAssistantProcessesResult = {
  data: [mockAssistantProcess],
  isLoading: false,
  error: null,
};

export const mockDeviceDocumentChange: AssistantMultiStepFormStep = {
  dataKeyId: "intended-use",
  templateKey: TEMPLATE_ID.INTENDED_USE,
};
